import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | pending | failed
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    const maxAttempts = 6;
    let cancelled = false;

    const poll = async (currentAttempt) => {
      if (cancelled) return;
      if (currentAttempt >= maxAttempts) {
        setStatus("pending");
        return;
      }
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          // Google Ads conversion — fire only once per session, with real amount
          try {
            if (
              typeof window.gtag === "function" &&
              !window.__mdmConversionFired
            ) {
              window.__mdmConversionFired = true;
              const amount = Number(res.data.amount_total || 0) / 100;
              const currency = (res.data.currency || "eur").toUpperCase();
              window.gtag("event", "conversion", {
                send_to: "AW-18250688484/C6eqCOqXs8EcEOTPzf5D",
                value: amount > 0 ? amount : 1.0,
                currency: currency,
                transaction_id: sessionId,
              });
            }
          } catch (e) {
            // Silent — tracking must never break the user experience
          }
          return;
        }
        if (res.data.status === "expired") {
          setStatus("failed");
          return;
        }
        setAttempts(currentAttempt + 1);
        setTimeout(() => poll(currentAttempt + 1), 2000);
      } catch (err) {
        setStatus("failed");
      }
    };

    poll(0);
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <main
      data-testid="success-page-root"
      className="min-h-screen bg-[#F9F8F6] text-[#0B192C] flex items-center justify-center px-6"
    >
      <div className="max-w-xl text-center">
        {status === "checking" && (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-[#0B192C]" />
            <p className="overline mt-6">Verifica pagamento</p>
            <h1 className="font-serif-display text-4xl md:text-5xl mt-3">
              Stiamo confermando il tuo ordine...
            </h1>
            <p className="mt-4 text-[#3A4F6B]">Tentativo {attempts + 1} di 6</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="mx-auto h-16 w-16 border border-[#0B192C] flex items-center justify-center">
              <Check className="h-8 w-8" strokeWidth={1.2} />
            </div>
            <p className="overline mt-8">Ordine Confermato</p>
            <h1 className="font-serif-display text-4xl md:text-6xl mt-3 leading-tight">
              Benvenuto a bordo.
            </h1>
            <p className="mt-6 text-[#3A4F6B] text-lg font-light leading-relaxed">
              Il tuo viaggio con <em>Metodo di Mare</em> è ufficialmente iniziato.
              A breve riceverai una conferma con tutti i dettagli per accedere al
              libro e ai bonus.
            </p>
            <Link
              to="/"
              data-testid="success-back-home"
              className="inline-block mt-10 px-8 py-4 bg-[#0B192C] text-[#F9F8F6] text-sm uppercase tracking-[0.2em] hover:bg-[#1E3557] transition-colors"
            >
              Torna alla Home
            </Link>
          </>
        )}
        {status === "pending" && (
          <>
            <p className="overline mt-6">In elaborazione</p>
            <h1 className="font-serif-display text-4xl md:text-5xl mt-3">
              Pagamento in elaborazione
            </h1>
            <p className="mt-4 text-[#3A4F6B]">
              Riceverai un'email non appena sarà confermato.
            </p>
            <Link
              to="/"
              data-testid="success-back-home"
              className="inline-block mt-8 px-8 py-4 border border-[#0B192C] text-[#0B192C] text-sm uppercase tracking-[0.2em] hover:bg-[#0B192C] hover:text-[#F9F8F6] transition-colors"
            >
              Torna alla Home
            </Link>
          </>
        )}
        {status === "failed" && (
          <>
            <p className="overline mt-6 text-[#9C3838]">Errore</p>
            <h1 className="font-serif-display text-4xl md:text-5xl mt-3">
              Pagamento non confermato
            </h1>
            <p className="mt-4 text-[#3A4F6B]">
              Qualcosa è andato storto. Per favore, riprova o contattaci.
            </p>
            <Link
              to="/"
              data-testid="success-back-home"
              className="inline-block mt-8 px-8 py-4 border border-[#0B192C] text-[#0B192C] text-sm uppercase tracking-[0.2em] hover:bg-[#0B192C] hover:text-[#F9F8F6] transition-colors"
            >
              Riprova
            </Link>
          </>
        )}
      </div>
    </main>
  );
}
